<template>
	<div>
        <div class="flex justify-between mb-5 border-b border-gray-300 pb-2">
            <div class="flex items-center justify-between w-full">
                <h1 class="module-header">Lease vs. Purchase Calculator</h1>
            </div>
        </div>
        <div class="flex">
        	<div class="w-2/5 mr-16">
				<div class="w-full border-gray-300er rounded">
					<h3 class="text-blue-600">Purchase Calculation</h3>
					<table class="w-full shadow-none">
						<tbody>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Total Sq. Ft.</td>
								<td></td>
								<td class="text-sm text-right pr-5">{{ comp.building.total_square_footage | number }}</td>
								<td class="border-l border-gray-300 text-right font-bold">PSF</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Sale Price</td>
								<td></td>
								<td class="text-right pr-2">
									<currency-input v-model="salePrice" :size="11" justify-end></currency-input>
								</td>
								<td :data-precision="2" :data-originalvalue="salePricePSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(salePricePSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Sale Discount (%)</td>
								<td class="text-right">
									<percent-input v-model="saleDiscountPercentage" :size="3"></percent-input>
								</td>
								<td :data-precision="0" :data-originalvalue="salePriceMinusDiscount" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(salePriceMinusDiscount) }}
								</td>
								<td :data-precision="2" :data-originalvalue="salePriceMinusDiscountPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(salePriceMinusDiscountPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Eco Dev Discount</td>
								<td class="text-right">
									<percent-input v-model="economicDeveloperDiscountPercentage" :size="3"></percent-input>
								</td>
								<td :data-precision="0" :data-originalvalue="salePriceMinusEcoDevDiscount" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(salePriceMinusEcoDevDiscount) }}
								</td>
								<td :data-precision="2" :data-originalvalue="salePriceMinusEcoDevDiscountPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(salePriceMinusEcoDevDiscountPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Adjusted Sale Price</td>
								<td></td>
								<td :data-precision="0" :data-originalvalue="adjustedSalePrice" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(adjustedSalePrice) }}
								</td>
								<td :data-precision="2" :data-originalvalue="adjustedSalePricePSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(adjustedSalePricePSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Closing Cost (%)</td>
								<td class="text-right">
									<percent-input v-model="closingCostPercentage" :size="3"></percent-input>
								</td>
								<td :data-precision="0" :data-originalvalue="closingCosts" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(closingCosts) }}
								</td>
								<td :data-precision="2" :data-originalvalue="closingCostsPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(closingCostsPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Subtotal</td>
								<td></td>
								<td :data-precision="0" :data-originalvalue="subtotal" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(subtotal) }}
								</td>
								<td :data-precision="2" :data-originalvalue="subtotalPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(subtotalPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Renovation Costs</td>
								<td></td>
								<td class="text-right pr-2">
									<currency-input v-model="renovationCosts" justify-end></currency-input>
								</td>
								<td :data-precision="2" :data-originalvalue="renovationCostsPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(renovationCostsPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Other Costs</td>
								<td></td>
								<td class="text-right pr-2">
									<currency-input v-model="otherCosts" justify-end></currency-input>
								</td>
								<td :data-precision="2" :data-originalvalue="otherCostsPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(otherCostsPSF, 2) }}
								</td>
							</tr>
							<tr class="border-b border-green-500">
								<td class="text-sm font-medium py-6 pl-2">Grand Total</td>
								<td></td>
								<td :data-precision="0" :data-originalvalue="grandTotal" class="is-currency text-xl text-right font-medium pr-5">
									{{ formatCurrency(grandTotal) }}
								</td>
								<td :data-precision="2" :data-originalvalue="grandTotalPSF" class="is-currency text-sm text-right font-medium border-l border-gray-300">
									{{ formatCurrency(grandTotalPSF, 2) }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div class="flex flex-col w-3/5">
				<div class="flex mb-8">
					<div class="w-1/3">
						<div class="mr-3 border border-gray-300 rounded-lg">
							<h3 class="text-blue-600 pt-4 px-2 text-center">Debt Service</h3>
							<table class="w-full shadow-none">
								<tbody>
									<tr class="border-b border-green-500">
										<td class="text-sm font-medium py-6 pl-2">Interest (%)</td>
										<td class="text-right text-lg">
											<percent-input v-model="debtServiceInterestPercentage" :size="4"></percent-input>
										</td>
									</tr>
									<tr>
										<td class="text-sm font-medium py-6 pl-2">Term (Years)</td>
										<td class="text-right text-lg">
											<input type="text" size="4" class="form-input text-right" v-model="debtServiceLeaseTermYears">
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div class="w-3/5 border border-gray-300 rounded max-w-sm">
						<h3 class="text-blue-600 pt-4 px-2 text-center">Amount of Equity Required</h3>
						<table class="w-full shadow-none">
							<tbody>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Equity %</td>
									<td class="text-right">
										<percent-input v-model="equityPercentage" :size="4"></percent-input>
									</td>
									<td :data-precision="0" :data-originalvalue="equityRequired" class="is-currency text-sm text-right pr-2">
										{{ formatCurrency(equityRequired) }}
									</td>
									<td :data-precision="2" :data-originalvalue="equityRequiredPSF" class="is-currency text-sm text-right border-l border-gray-300">
										{{ formatCurrency(equityRequiredPSF, 2) }}
									</td>
								</tr>
								<tr>
									<td class="text-sm font-medium py-6 pl-2">Debt</td>
									<td></td>
									<td :data-precision="0" :data-originalvalue="debtTotal" class="is-currency text-sm text-right pr-2">
										{{ formatCurrency(debtTotal) }}
									</td>
									<td :data-precision="2" :data-originalvalue="debtTotalPSF" class="is-currency text-sm text-right border-l border-gray-300">
										{{ formatCurrency(debtTotalPSF, 2) }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div>
					<div class="w-full border-gray-300er rounded">
						<h3 class="text-blue-600">Lease Calculation</h3>
						<table class="w-full max-w-md shadow-none">
							<tbody>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Debt Cost</td>
									<td></td>
									<td :data-precision="0" :data-originalvalue="debtCost" class="is-currency text-sm text-right pr-5">
										{{ formatCurrency(debtCost) }}
									</td>
									<td :data-precision="2" :data-originalvalue="debtCostPSF" class="is-currency text-sm text-right border-l border-gray-300">
										{{ formatCurrency(debtCostPSF, 2) }}
									</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Operating Expenses</td>
									<td></td>
									<td class="text-right pr-2">
										<currency-input v-model="operatingExpenses" justify-end></currency-input>
									</td>
									<td :data-precision="2" :data-originalvalue="operatingExpensesPSF" class="is-currency text-right border-l border-gray-300">
										{{ formatCurrency(operatingExpensesPSF, 2) }}
									</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Taxes</td>
									<td></td>
									<td class="text-right pr-2">
										<currency-input v-model="taxes" justify-end></currency-input>
									</td>
									<td :data-precision="2" :data-originalvalue="taxesPSF" class="is-currency text-right border-l border-gray-300">
										{{ formatCurrency(taxesPSF, 2) }}
									</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Subtotal</td>
									<td></td>
									<td :data-precision="0" :data-originalvalue="debtSubtotal" class="is-currency text-sm text-right pr-5">
										{{ formatCurrency(debtSubtotal) }}
									</td>
									<td :data-precision="2" :data-originalvalue="debtSubtotalPSF" class="is-currency text-sm text-right border-l border-gray-300">
										{{ formatCurrency(debtSubtotalPSF, 2) }}
									</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">ROI on LL Equity</td>
									<td class="text-right">
										<percent-input v-model="roiOnLLEquityPercentage" :size="3"></percent-input>
									</td>
								<td :data-precision="0" :data-originalvalue="roiOnLLEquity" class="is-currency text-sm text-right pr-5">
									{{ formatCurrency(roiOnLLEquity) }}
								</td>
								<td :data-precision="2" :data-originalvalue="roiOnLLEquityPSF" class="is-currency text-sm text-right border-l border-gray-300">
									{{ formatCurrency(roiOnLLEquityPSF, 2) }}
								</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Amortized Down</td>
									<td></td>
									<td class="text-right pr-2">
										<currency-input v-model="amortizedDownPayment" justify-end></currency-input>
									</td>
									<td :data-precision="2" :data-originalvalue="amortizedDownPaymentPSF" class="is-currency text-sm text-right border-l border-gray-300">
										{{ formatCurrency(amortizedDownPaymentPSF, 2) }}
									</td>
								</tr>
								<tr class="border-b border-green-500">
									<td class="text-sm font-medium py-6 pl-2">Equivalent Lease $</td>
									<td></td>
									<td :data-precision="0" :data-originalvalue="equivalentLease" class="is-currency text-xl text-right font-medium pr-5">
										{{ formatCurrency(equivalentLease) }}
									</td>
									<td :data-precision="2" :data-originalvalue="equivalentLeasePSF" class="is-currency text-sm text-right font-medium border-l border-gray-300">
										{{ formatCurrency(equivalentLeasePSF, 2) }}
									</td>
								</tr>
							</tbody>
						</table>
						<button @click="save" class="max-w-md btn btn-green w-full mt-4">Save</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		props: {
			compData: {
				type: [Array, Object],
				required: true
			},

			calculationData: {
				type: [Array, Object],
				required: true
			}
		},

		data() {
			return {
				salePrice: this.calculationData.sale_price,
				saleDiscountPercentage: this.calculationData.sale_discount_percentage,
				economicDeveloperDiscountPercentage: this.calculationData.economic_developer_discount_percentage,
				closingCostPercentage: this.calculationData.closing_cost_percentage,
				renovationCosts: this.calculationData.renovation_costs,
				otherCosts: this.calculationData.other_costs,
				debtServiceInterestPercentage: this.calculationData.debt_service_interest_percentage,
				debtServiceLeaseTermYears: this.calculationData.debt_service_lease_term_years,
				equityPercentage: this.calculationData.equity_percentage,
				operatingExpenses: this.calculationData.operating_expenses,
				taxes: this.calculationData.taxes,
				roiOnLLEquityPercentage: this.calculationData.roi_on_ll_equity_percentage,
				amortizedDownPayment: this.calculationData.amortized_down_payment,
			}
		},

		computed: {
			comp() {
				return this.compData;
			},

			salePriceCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.salePrice);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			salePricePSF() {				
				return isNaN(this.salePriceCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.salePriceCleansed / this.comp.building.total_square_footage) * 100;
			},

			saleDiscountPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.saleDiscountPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			salePriceMinusDiscount() {				
				return isNaN(this.salePriceCleansed)
					? 0
					: (this.salePriceCleansed * this.saleDiscountPercentageCleansed) * 100;
			},

			salePriceMinusDiscountPSF() {			
				return isNaN(this.salePriceMinusDiscount) || this.comp.building.total_square_footage == 0
					? 0
					: (this.salePriceMinusDiscount / this.comp.building.total_square_footage);
			},

			economicDeveloperDiscountPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.economicDeveloperDiscountPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			salePriceMinusEcoDevDiscount() {				
				return isNaN(this.salePriceCleansed)
					? 0
					: (this.salePriceCleansed * this.economicDeveloperDiscountPercentageCleansed) * 100;
			},

			salePriceMinusEcoDevDiscountPSF() {			
				return isNaN(this.salePriceMinusEcoDevDiscount) || this.comp.building.total_square_footage == 0
					? 0
					: (this.salePriceMinusEcoDevDiscount / this.comp.building.total_square_footage);
			},

			adjustedSalePrice() {
				return (this.salePriceCleansed - (this.salePriceMinusDiscount / 100) - (this.salePriceMinusEcoDevDiscount / 100)) * 100;
			},

			adjustedSalePricePSF() {
				return isNaN(this.adjustedSalePrice) || this.comp.building.total_square_footage == 0
					? 0
					: (this.adjustedSalePrice / this.comp.building.total_square_footage);
			},

			closingCostPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.closingCostPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			closingCosts() {
				return (this.closingCostPercentageCleansed * this.salePriceCleansed) * 100;
			},

			closingCostsPSF() {
				return isNaN(this.closingCosts) || this.comp.building.total_square_footage == 0
					? 0
					: (this.closingCosts / this.comp.building.total_square_footage);
			},

			subtotal() {
				return this.adjustedSalePrice + this.closingCosts;
			},

			subtotalPSF() {
				return isNaN(this.subtotal) || this.comp.building.total_square_footage == 0
					? 0
					: (this.subtotal / this.comp.building.total_square_footage);
			},

			renovationCostsCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.renovationCosts);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			renovationCostsPSF() {				
				return isNaN(this.renovationCostsCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.renovationCostsCleansed / this.comp.building.total_square_footage) * 100;
			},

			otherCostsCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.otherCosts);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			otherCostsPSF() {				
				return isNaN(this.otherCostsCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.otherCostsCleansed / this.comp.building.total_square_footage) * 100;
			},

			grandTotal() {
				return (this.subtotal + (this.renovationCostsCleansed * 100) + (this.otherCostsCleansed * 100));
			},

			grandTotalPSF() {
				return isNaN(this.grandTotal) || this.comp.building.total_square_footage == 0
					? 0
					: (this.grandTotal / this.comp.building.total_square_footage);
			},

			interestPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.debtServiceInterestPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			leaseTermMonths() {
				return isNaN(this.debtServiceLeaseTermYears) ? 0 : this.debtServiceLeaseTermYears * 12
			},

			equityPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.equityPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			equityRequired() {
				return (this.equityPercentageCleansed * this.grandTotal);
			},

			equityRequiredPSF() {
				return isNaN(this.equityRequired) || this.comp.building.total_square_footage == 0
					? 0
					: (this.equityRequired / this.comp.building.total_square_footage);
			},

			debtTotal() {
				return (this.grandTotal - this.equityRequired);
			},

			debtTotalPSF() {
				return isNaN(this.debtTotal) || this.comp.building.total_square_footage == 0
					? 0
					: (this.debtTotal / this.comp.building.total_square_footage);
			},

			debtCost() {
				const pmt = Number.prototype.pmt(this.debtTotal, Number.prototype.cleanseInteger(this.debtServiceInterestPercentage), this.leaseTermMonths)
				return pmt * 12;
			},

			debtCostPSF() {
				return isNaN(this.debtCost) || this.comp.building.total_square_footage == 0
					? 0
					: (this.debtCost / this.comp.building.total_square_footage);
			},

			operatingExpensesCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.operatingExpenses);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			operatingExpensesPSF() {
				return isNaN(this.operatingExpensesCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.operatingExpensesCleansed / this.comp.building.total_square_footage) * 100;
			},

			taxesCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.taxes);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			taxesPSF() {
				return isNaN(this.taxesCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.taxesCleansed / this.comp.building.total_square_footage) * 100;
			},

			debtSubtotal() {
				return (this.debtCost + (this.operatingExpensesCleansed * 100) + (this.taxesCleansed * 100));
			},

			debtSubtotalPSF() {
				return isNaN(this.debtSubtotal) || this.comp.building.total_square_footage == 0
					? 0
					: (this.debtSubtotal / this.comp.building.total_square_footage);
			},

			roiOnLLEquityPercentageCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.roiOnLLEquityPercentage);
				
				return isNaN(cleansed) ? 0 : cleansed / 100;
			},

			roiOnLLEquity() {
				return (this.roiOnLLEquityPercentageCleansed * this.equityRequired);
			},

			roiOnLLEquityPSF() {
				return isNaN(this.roiOnLLEquity) || this.comp.building.total_square_footage == 0
					? 0
					: (this.roiOnLLEquity / this.comp.building.total_square_footage);
			},

			amortizedDownPaymentCleansed() {
				const cleansed = Number.prototype.cleanseInteger(this.amortizedDownPayment);
				
				return isNaN(cleansed) ? 0 : cleansed;
			},

			amortizedDownPaymentPSF() {
				return isNaN(this.amortizedDownPaymentCleansed) || this.comp.building.total_square_footage == 0
					? 0
					: (this.amortizedDownPaymentCleansed / this.comp.building.total_square_footage) * 100;
			},

			equivalentLease() {
				return (this.debtSubtotal + this.roiOnLLEquity + (this.amortizedDownPaymentCleansed * 100));
			},

			equivalentLeasePSF() {
				return isNaN(this.equivalentLease) || this.comp.building.total_square_footage == 0
					? 0
					: (this.equivalentLease / this.comp.building.total_square_footage);
			},
		},

		methods: {
            save() {
                axios.post('/projects/' + this.comp.project_id + '/comps/' + this.comp.id + '/lease-vs-purchase-calculation', {
                	comp_id: this.comp.id,
					sale_price: this.salePriceCleansed,
            		sale_discount_percentage: Number.prototype.cleanseFloat(this.saleDiscountPercentage),
            		economic_developer_discount_percentage: Number.prototype.cleanseFloat(this.economicDeveloperDiscountPercentage),
            		closing_cost_percentage: Number.prototype.cleanseFloat(this.closingCostPercentage),
            		renovation_costs: this.renovationCostsCleansed,
            		other_costs: this.otherCostsCleansed,
            		debt_service_interest_percentage: Number.prototype.cleanseFloat(this.debtServiceInterestPercentage),
            		debt_service_lease_term_years: this.debtServiceLeaseTermYears,
            		equity_percentage: Number.prototype.cleanseFloat(this.equityPercentage),
            		operating_expenses: this.operatingExpensesCleansed,
            		taxes: this.taxesCleansed,
            		roi_on_ll_equity_percentage: Number.prototype.cleanseFloat(this.roiOnLLEquityPercentage),
            		amortized_down_payment: this.amortizedDownPaymentCleansed
                }).then((response) => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    }); 
                }).catch((response) => {
                    console.error(response);

                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: response.data.message
                    }); 
                });
            },
		},
	}
</script>